import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Shield, Zap, Headphones, CreditCard } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import PricingCard from "../../components/AdminPricingCard/AdminPricingCard";
import Button from "../../components/Button/Button";
import Notification from "../../components/Notification/Notification";
import AddCreditSection from "./AddCreditSection";

/* ===================== ASSETS ===================== */
import powerIcon from "../../assets/images/power.svg";
import starterIcon from "../../assets/images/star_icon.svg";
import startsignIcon from "../../assets/images/star_sign.svg";

/* ===================== SERVICES ===================== */
import { apiGet, apiPost } from "../../apiService";
import { getErrorMessage } from "../../utils/errorHelper";

/* ===================== STYLES ===================== */
import styles from "./AdminDashboardPricing.module.scss";

export const iconMap = {
  basic_icon: starterIcon,
  pro_icon: startsignIcon,
};

const AdminDashboardPricing = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [notification, setNotification] = useState(null);
  const [isPaying, setIsPaying] = useState(false);

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const fetchPlans = useCallback(async () => {
    try {
      const res = await apiGet("catalog", "/plans");
      setPlans(res?.data?.plans || []);
    } catch (err) {
      console.error("Failed to fetch plans", err);
      setPlans([]);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleEnterpriseClick = () => {
    const token = localStorage.getItem("authToken");
    if (token) {
      navigate("/billing/enterprise-request");
    } else {
      navigate("/pricing/enterprise-request");
    }
  };

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const isLoggedIn = () => {
    const token = localStorage.getItem("authToken");
    const user = localStorage.getItem("user");
    return !!(token && user);
  };

  const handleBuyNow = async (plan) => {
    if (isPaying) return;
    if (!isLoggedIn()) {
      localStorage.setItem("pendingPlan", JSON.stringify(plan));
      navigate("/signup");
      return;
    }
    try {
      const profileRes = await apiGet("user", "/profile");
      if (profileRes?.status !== "success") {
        throw new Error("Profile fetch failed");
      }
      const profile = profileRes.data;
      if (profile?.companyName) {
        localStorage.setItem("companyName", profile.companyName);
      }
      if (
        !profile.address ||
        !profile.state ||
        !profile.gstNumber ||
        profile.address.trim() === "" ||
        profile.gstNumber.trim() === ""
      ) {
        setNotification({
          type: "error",
          message:
            "Please update Address, State and GST Number in your profile before purchasing.",
        });
        setTimeout(() => navigate("/user-profile"), 2000);
        return;
      }
      openRazorpay(plan);
    } catch (error) {
      console.error("Profile validation error:", error);
      setNotification({
        type: "error",
        message: getErrorMessage(error, "Unable to validate profile."),
      });
    }
  };

  const openRazorpay = async (plan) => {
    if (isPaying) return;
    setIsPaying(true);
    const isLoaded = await loadRazorpay();
    if (!isLoaded) {
      setIsPaying(false);
      setNotification({
        type: "error",
        message: "Razorpay SDK failed to load",
      });
      return;
    }
    const user = JSON.parse(localStorage.getItem("user")) || {};
    try {
      const orderRes = await apiPost("billing", "/create-order", {
        planId: plan.id,
      });
      const backendOrderId = orderRes?.data?.orderId;
      if (!backendOrderId) {
        throw new Error(JSON.stringify({ message: "Failed to create order" }));
      }
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: orderRes.data.amount,
        currency: "INR",
        name: user.companyName || user.name || "My Company",
        description: plan.planName,
        order_id: backendOrderId,
        handler: async function (response) {
          try {
            const verifyRes = await apiPost("billing", "/verify-payment", {
              orderId: backendOrderId,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature,
            });
            if (verifyRes?.data?.status === "Success") {
              setTimeout(() => {
                window.dispatchEvent(new Event("balance-updated"));
              }, 500);
              setIsPaying(false);
              navigate("/billing/payment-success", {
                replace: true,
                state: { payment: verifyRes.data },
              });
              return;
            }
            setIsPaying(false);
            navigate("/billing/payment-failed", {
              replace: true,
              state: {
                errorMessage:
                  verifyRes?.data?.message || "Payment verification failed",
              },
            });
          } catch (err) {
            setIsPaying(false);
            navigate("/billing/payment-failed", {
              replace: true,
              state: {
                errorMessage: getErrorMessage(err, "Payment failed"),
              },
            });
          }
        },
        modal: {
          ondismiss: function () {
            setIsPaying(false);
            setNotification({
              type: "info",
              message: "Payment cancelled",
            });
          },
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", function (res) {
        setIsPaying(false);
        setNotification({
          type: "error",
          message:
            res?.error?.description || res?.error?.reason || "Payment failed",
        });
      });
      rzp.open();
    } catch (error) {
      setIsPaying(false);
      setNotification({
        type: "error",
        message: getErrorMessage(error, "Payment failed"),
      });
    }
  };

  return (
    <div className={styles["admin-pricing__page"]}>
      {notification && (
        <div className={styles["admin-pricing__toast-container"]}>
          <Notification
            type={notification.type}
            message={notification.message}
          />
        </div>
      )}

      {/* HEADER SECTION */}
      <div className={styles["admin-pricing__header"]}>
        <div className={styles["admin-pricing__icon-wrapper"]}>
          <CreditCard size={24} strokeWidth={2.5} />
        </div>
        <div className={styles["admin-pricing__title-group"]}>
          <h2 className={styles["admin-pricing__title"]}>Buy API Credits</h2>
          <p className={styles["admin-pricing__subtitle"]}>
            Top up your balance anytime. Use credits as you go, with no lock-in.
          </p>
        </div>
        <div className={styles["admin-pricing__actions"]}>
          <Button
            text="Request Plan"
            icon={Headphones}
            onClick={handleEnterpriseClick}
            className="btn_secondary"
          />
        </div>
      </div>

      {/* DASHBOARD CARD 1: INFO */}
      <div className={styles["admin-pricing__card"]}>
        <div className={styles["admin-pricing__info-grid"]}>
          <div className={styles["admin-pricing__rate-box"]}>
            <span className={styles["admin-pricing__rate-box-label"]}>
              Unit Price
            </span>
            <div className={styles["admin-pricing__rate-box-value"]}>
              <span>1 credit</span>
              <span>=</span>
              <span>₹10</span>
            </div>
            <p className={styles["admin-pricing__rate-box-desc"]}>
              Transparent pay-as-you-go. Add credits whenever you need them.
            </p>
          </div>

          <div className={styles["admin-pricing__features"]}>
            <div className={styles["admin-pricing__features-title"]}>
              <Zap size={20} strokeWidth={2.5} />
              Pay per use
            </div>
            <ul>
              <li>
                <Check size={18} strokeWidth={2.5} />
                No monthly or yearly commitment
              </li>
              <li>
                <Check size={18} strokeWidth={2.5} />
                No long-term contracts required
              </li>
              <li>
                <Check size={18} strokeWidth={2.5} />
                Billed in credits for easy tracking
              </li>
              <li>
                <Check size={18} strokeWidth={2.5} />
                Top up your balance anytime
              </li>
            </ul>
          </div>
        </div>

        <div className={styles["admin-pricing__gst-strip"]}>
          <Shield size={20} strokeWidth={2} />
          <span>
            Listed pack prices are <strong>exclusive of 18% GST</strong> (as
            applicable). You’ll see the tax breakdown at checkout.
          </span>
        </div>
      </div>

      {/* DASHBOARD CARD 2: ADD CREDITS (base plan or addon picker) */}
      <AddCreditSection onNotify={setNotification} />

      {/* DASHBOARD CARD 3: PACKS */}
      {/* {plans.length > 0 && (
        <div className={styles["admin-pricing__card"]}>
          <h3 className={styles["admin-pricing__card-title"]}>
            Choose a Credit Pack
          </h3>

          <div className={styles["admin-pricing__packs-grid"]}>
            {plans.map((plan) => (
              <PricingCard
                key={plan.id}
                title={plan.planName}
                price={`₹${plan.price}`}
                credits={`${plan.credits.toLocaleString()} credits`}
                popular={plan.isPopular}
                buttonText={isPaying ? "Processing..." : "Buy now"}
                icon={iconMap[plan.iconName] || starterIcon}
                onClick={() => handleBuyNow(plan)}
                GstText="Exclusive of 18% GST"
              />
            ))}

            <PricingCard
              title="Enterprise pack"
              price="Custom"
              credits="Custom credits & pricing"
              buttonText="Contact us"
              icon={powerIcon}
              onClick={handleEnterpriseClick}
            />
          </div>
        </div>
      )} */}
    </div>
  );
};

export default AdminDashboardPricing;
